import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Save, Download, TrendingUp, ShieldCheck, Settings2, ArrowRight, Zap } from "lucide-react";
import { api, apiError, money, formatDateTime, FEATURE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const FEE_TYPE_LABEL = { percentage: "Percentual", fixed: "Fixa", percentage_fixed: "Percentual + Fixa" };
const fmtFee = (kind, v) => (v === null || v === undefined ? "—" : kind === "percent" ? `${v}%` : kind === "enum" ? (FEE_TYPE_LABEL[v] || v) : money(v));

const SRC_LABEL = { subscription: "Assinatura", admin_manual: "Manual", courtesy: "Cortesia", promotion: "Promoção", trial: "Trial", system: "Gratuito" };

export default function AdminPlans() {
  const { user } = useAuth();
  const canWrite = user?.permissions?.includes("plans:write");
  const [sub, setSub] = useState("planos");
  const [plans, setPlans] = useState([]);
  const [subs, setSubs] = useState(null);
  const [audit, setAudit] = useState([]);
  const [payer, setPayer] = useState(null);
  const [fin, setFin] = useState(null);
  const [feeCfg, setFeeCfg] = useState(null);      // { config, fields, can_edit }
  const [feeDraft, setFeeDraft] = useState(null);
  const [feeAudit, setFeeAudit] = useState([]);
  const [scoreCfg, setScoreCfg] = useState(null);
  const [scoreDraft, setScoreDraft] = useState(null);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const dirty = useRef(false);

  const loadPlans = useCallback(() => api.get("/admin/plans").then((r) => { if (!dirty.current) setPlans(r.data); }).catch((e) => toast.error(apiError(e.response?.data?.detail))), []);
  const loadFees = useCallback(() => {
    api.get("/admin/financial-config").then((r) => { setFeeCfg(r.data); setFeeDraft({ ...r.data.config }); }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
    api.get("/admin/financial-config/audit").then((r) => setFeeAudit(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadPlans(); }, [loadPlans]);
  useEffect(() => {
    if (sub === "assinaturas") api.get("/admin/subscriptions").then((r) => setSubs(r.data)).catch(() => {});
    if (sub === "financeiro") Promise.all([api.get("/admin/reports/financial"), api.get("/admin/subscriptions")]).then(([f, s]) => setFin({ ...f.data.totals, ...s.data })).catch((e) => toast.error(apiError(e.response?.data?.detail)));
    if (sub === "auditoria") api.get("/admin/plans/audit").then((r) => setAudit(r.data)).catch(() => {});
    if (sub === "selo") api.get("/admin/payer-config").then((r) => setPayer(r.data)).catch(() => {});
    if (sub === "taxas") loadFees();
    if (sub === "score") api.get("/admin/score-config").then((r) => { setScoreCfg(r.data); setScoreDraft({ ...r.data.config }); }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [sub, loadFees]);

  const savePlan = async (p) => {
    try {
      await api.put(`/admin/plans/${p.slug}`, { name: p.name, description: p.description, price: parseFloat(p.price) || 0,
        event_limit: parseInt(p.event_limit), priority_notifications: !!p.priority_notifications,
        transaction_fee: parseFloat(p.transaction_fee) || 0, active: !!p.active, features: p.features,
        rep_groups_limit: parseInt(p.rep_groups_limit), rep_songs_per_group: parseInt(p.rep_songs_per_group), rep_songs_total: parseInt(p.rep_songs_total),
        entitlements: p.entitlements || [] });
      toast.success(`Plano ${p.name} salvo.`); dirty.current = false; loadPlans();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const savePayer = async () => {
    try { await api.post("/admin/payer-config", payer); toast.success("Critérios do selo salvos."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const exportCsv = () => { window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/plans/audit?format=csv`, "_blank"); };

  const feeFieldMeta = (k) => (feeCfg?.fields || []).find((f) => f.key === k) || {};
  const setFeeField = (k, v) => setFeeDraft((d) => ({ ...d, [k]: v }));
  const openFeeConfirm = () => {
    if (!feeDraft || !feeCfg) return;
    const changes = [];
    for (const f of feeCfg.fields) {
      const nv = ["enum", "text"].includes(f.kind) ? feeDraft[f.key] : (parseFloat(feeDraft[f.key]) || 0);
      const cur = ["enum", "text"].includes(f.kind) ? feeCfg.config[f.key] : (parseFloat(feeCfg.config[f.key]) || 0);
      if (`${nv}` !== `${cur}`) changes.push({ ...f, from: cur, to: nv });
    }
    if (changes.length === 0) { toast.info("Nenhuma alteração para salvar."); return; }
    setPendingChanges(changes); setConfirmOpen(true);
  };
  const confirmSaveFees = async () => {
    try {
      const body = {};
      pendingChanges.forEach((c) => { body[c.key] = c.to; });
      const r = await api.put("/admin/financial-config", body);
      toast.success(`Taxas atualizadas (${r.data.changed?.length || 0} alteração(ões)).`);
      setConfirmOpen(false); setPendingChanges([]); loadFees();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const setField = (i, k, v) => { dirty.current = true; setPlans((p) => p.map((x, idx) => idx === i ? { ...x, [k]: v } : x)); };

  const tabs = [["planos", "Planos"], ["taxas", "⚙ Taxas"], ["score", "Score"], ["financeiro", "Financeiro"], ["assinaturas", "Assinaturas"], ["auditoria", "Auditoria"], ["selo", "Selo Bom Pagador"]];

  const saveScore = async () => {
    try { const r = await api.put("/admin/score-config", scoreDraft); toast.success(`Critérios de Score salvos (${r.data.changed?.length || 0}).`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-plans">
      <div className="flex gap-2 flex-wrap">
        {tabs.map(([id, l]) => (
          <button key={id} data-testid={`plans-subtab-${id}`} onClick={() => setSub(id)}
            className={`text-xs px-3 py-1.5 rounded-xl border ${sub === id ? "border-[#FF4D00] text-[#FF4D00] bg-[#FF4D00]/10" : "border-white/10 text-zinc-400"}`}>{l}</button>
        ))}
      </div>

      {sub === "planos" && (
        <div className="grid md:grid-cols-2 gap-3">
          {plans.map((p, i) => (
            <div key={p.slug} data-testid={`plan-config-${p.slug}`} className="bg-card border border-white/[0.06] rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between">
                <Input value={p.name} onChange={(e) => setField(i, "name", e.target.value)} disabled={!canWrite} className="bg-[#09090B] border-white/10 rounded-xl font-bold max-w-[60%]" />
                <div className="flex items-center gap-2 text-xs"><span className="text-zinc-500">Ativo</span><Switch checked={!!p.active} onCheckedChange={(v) => setField(i, "active", v)} disabled={!canWrite} /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-[11px] text-zinc-500">Preço (R$/mês)</Label><Input type="number" step="0.01" value={p.price} onChange={(e) => setField(i, "price", e.target.value)} disabled={!canWrite} className="bg-[#09090B] border-white/10 rounded-xl mt-1" /></div>
                <div><Label className="text-[11px] text-zinc-500">Limite eventos/mês (-1 = ∞)</Label><Input type="number" value={p.event_limit} onChange={(e) => setField(i, "event_limit", e.target.value)} disabled={!canWrite} className="bg-[#09090B] border-white/10 rounded-xl mt-1" /></div>
                <div><Label className="text-[11px] text-zinc-500">Taxa da Plataforma por evento (R$)</Label><Input type="number" step="0.01" value={p.transaction_fee} onChange={(e) => setField(i, "transaction_fee", e.target.value)} disabled={!canWrite} className="bg-[#09090B] border-white/10 rounded-xl mt-1" /><p className="text-[10px] text-zinc-600 mt-1">Taxa aplicada por evento conforme o plano vigente do usuário.</p></div>
                <div className="flex items-end gap-2 text-xs"><Switch checked={!!p.priority_notifications} onCheckedChange={(v) => setField(i, "priority_notifications", v)} disabled={!canWrite} /><span className="text-zinc-400 pb-1">Notif. prioritárias</span></div>
              </div>
              <Input value={p.description} onChange={(e) => setField(i, "description", e.target.value)} disabled={!canWrite} placeholder="Descrição" className="bg-[#09090B] border-white/10 rounded-xl text-sm" />
              <div className="border-t border-white/[0.06] pt-2">
                <Label className="text-[11px] text-zinc-500">Limites de repertório (-1 = ilimitado)</Label>
                <div className="grid grid-cols-3 gap-2 mt-1.5">
                  <div><Label className="text-[10px] text-zinc-600">Grupos</Label><Input data-testid={`plan-rep-groups-${p.slug}`} type="number" value={p.rep_groups_limit ?? 0} onChange={(e) => setField(i, "rep_groups_limit", e.target.value)} disabled={!canWrite} className="bg-[#09090B] border-white/10 rounded-xl mt-0.5" /></div>
                  <div><Label className="text-[10px] text-zinc-600">Músicas/grupo</Label><Input data-testid={`plan-rep-pergroup-${p.slug}`} type="number" value={p.rep_songs_per_group ?? 0} onChange={(e) => setField(i, "rep_songs_per_group", e.target.value)} disabled={!canWrite} className="bg-[#09090B] border-white/10 rounded-xl mt-0.5" /></div>
                  <div><Label className="text-[10px] text-zinc-600">Total músicas</Label><Input data-testid={`plan-rep-total-${p.slug}`} type="number" value={p.rep_songs_total ?? 0} onChange={(e) => setField(i, "rep_songs_total", e.target.value)} disabled={!canWrite} className="bg-[#09090B] border-white/10 rounded-xl mt-0.5" /></div>
                </div>
              </div>
              <div className="border-t border-white/[0.06] pt-2">
                <Label className="text-[11px] text-zinc-500">Funcionalidades incluídas neste plano</Label>
                <div className="grid grid-cols-1 gap-1 mt-1.5 max-h-48 overflow-auto pr-1">
                  {Object.entries(FEATURE_LABELS).map(([k, lbl]) => {
                    const on = (p.entitlements || []).includes(k);
                    return (
                      <label key={k} className="flex items-center gap-2 text-[12px] text-zinc-300 cursor-pointer">
                        <input type="checkbox" data-testid={`plan-feat-${p.slug}-${k}`} checked={on} disabled={!canWrite}
                          onChange={(e) => setField(i, "entitlements", e.target.checked ? [...(p.entitlements || []), k] : (p.entitlements || []).filter((x) => x !== k))} />
                        {lbl}
                      </label>
                    );
                  })}
                </div>
              </div>
              {canWrite && <Button data-testid={`plan-save-${p.slug}`} size="sm" onClick={() => savePlan(p)} className="rounded-xl bg-[#FF4D00] hover:bg-[#E64500] gap-1 mt-1"><Save className="h-3.5 w-3.5" />Salvar</Button>}
            </div>
          ))}
        </div>
      )}

      {sub === "taxas" && feeCfg && feeDraft && (
        <div className="space-y-5" data-testid="fin-config">
          <div className="flex items-center gap-2 text-sm font-semibold"><Settings2 className="h-4 w-4 text-[#FF4D00]" />Configurações Financeiras</div>
          <p className="text-[11px] text-zinc-500 -mt-3 max-w-2xl">Configurações financeiras globais. A <b className="text-zinc-300">Taxa da Plataforma por evento</b> agora fica em <b className="text-zinc-300">Planos</b> (por plano). Alterações valem apenas para operações NOVAS — nada é recalculado retroativamente.{!feeCfg.can_edit && " Somente o Super Admin pode alterar."}</p>

          <div className="grid md:grid-cols-2 gap-3">
            <div className="bg-card border border-white/[0.06] rounded-xl p-4 space-y-3">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500">Gateway de pagamento</div>
              <div>
                <Label className="text-[11px] text-zinc-500">Taxa Pix (R$)</Label>
                <Input data-testid="fee-gateway-pix" type="number" step="0.01" value={feeDraft.gateway_pix_fee}
                  onChange={(e) => setFeeField("gateway_pix_fee", e.target.value)} disabled={!feeCfg.can_edit}
                  className="bg-[#09090B] border-white/10 rounded-xl mt-1" />
                <p className="text-[10px] text-zinc-600 mt-1">Taxa cobrada pelo gateway de pagamento e aplicada às novas operações Pix.</p>
              </div>
            </div>

            <div className="bg-card border border-white/[0.06] rounded-xl p-4 space-y-3">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500">Evento Extra</div>
              <div>
                <Label className="text-[11px] text-zinc-500">Valor (R$)</Label>
                <Input data-testid="fee-event-extra" type="number" step="0.01" value={feeDraft.event_extra_fee}
                  onChange={(e) => setFeeField("event_extra_fee", e.target.value)} disabled={!feeCfg.can_edit}
                  className="bg-[#09090B] border-white/10 rounded-xl mt-1" />
                <p className="text-[10px] text-zinc-600 mt-1">Regra independente para eventos acima do limite do plano.</p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-[#FF4D00]/20 rounded-xl p-4 space-y-3" data-testid="fee-express-card">
            <div className="text-[11px] uppercase tracking-widest text-[#FF4D00] flex items-center gap-1.5"><Zap className="h-3.5 w-3.5" />Em cima da Hora</div>
            <p className="text-[10px] text-zinc-600 -mt-1">Acréscimo somado POR CIMA do custo normal do evento, apenas nas chamadas ⚡ Em cima da Hora. Não altera as taxas de eventos comuns.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label className="text-[11px] text-zinc-500">Tipo do acréscimo</Label>
                <select data-testid="fee-express-type" value={feeDraft.express_surcharge_type || "fixed"}
                  onChange={(e) => setFeeField("express_surcharge_type", e.target.value)} disabled={!feeCfg.can_edit}
                  className="w-full h-10 bg-[#09090B] border border-white/10 rounded-xl px-3 text-sm mt-1">
                  <option value="fixed">Valor fixo (R$)</option>
                  <option value="percentage">Percentual (%)</option>
                  <option value="percentage_fixed">Percentual + fixo</option>
                </select>
              </div>
              <div>
                <Label className="text-[11px] text-zinc-500">Percentual (%)</Label>
                <Input data-testid="fee-express-percent" type="number" step="0.01" value={feeDraft.express_surcharge_percentage}
                  onChange={(e) => setFeeField("express_surcharge_percentage", e.target.value)} disabled={!feeCfg.can_edit}
                  className="bg-[#09090B] border-white/10 rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-[11px] text-zinc-500">Valor fixo (R$)</Label>
                <Input data-testid="fee-express-fixed" type="number" step="0.01" value={feeDraft.express_surcharge_fixed}
                  onChange={(e) => setFeeField("express_surcharge_fixed", e.target.value)} disabled={!feeCfg.can_edit}
                  className="bg-[#09090B] border-white/10 rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-[11px] text-zinc-500">Tempo de resposta (s)</Label>
                <Input data-testid="fee-express-response" type="number" value={feeDraft.express_response_seconds}
                  onChange={(e) => setFeeField("express_response_seconds", e.target.value)} disabled={!feeCfg.can_edit}
                  className="bg-[#09090B] border-white/10 rounded-xl mt-1" />
                <p className="text-[10px] text-zinc-600 mt-1">Janela para o músico aceitar antes do convite expirar.</p>
              </div>
              <div>
                <Label className="text-[11px] text-zinc-500">Raios progressivos (km)</Label>
                <Input data-testid="fee-express-radii" value={feeDraft.express_radii ?? ""} placeholder="5,10,20,30"
                  onChange={(e) => setFeeField("express_radii", e.target.value)} disabled={!feeCfg.can_edit}
                  className="bg-[#09090B] border-white/10 rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-[11px] text-zinc-500">Expansão do raio a cada (s)</Label>
                <Input data-testid="fee-express-expand" type="number" value={feeDraft.express_expand_seconds}
                  onChange={(e) => setFeeField("express_expand_seconds", e.target.value)} disabled={!feeCfg.can_edit}
                  className="bg-[#09090B] border-white/10 rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-[11px] text-zinc-500">Tolerância de cancelamento (min)</Label>
                <Input data-testid="fee-express-cancel-tol" type="number" value={feeDraft.express_cancel_tolerance_min}
                  onChange={(e) => setFeeField("express_cancel_tolerance_min", e.target.value)} disabled={!feeCfg.can_edit}
                  className="bg-[#09090B] border-white/10 rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-[11px] text-zinc-500">Antecedência mínima do evento planejado</Label>
                <select data-testid="fee-min-advance" value={String(feeDraft.event_min_advance_hours ?? 24)} disabled={!feeCfg.can_edit}
                  onChange={(e) => setFeeField("event_min_advance_hours", e.target.value)}
                  className="w-full h-10 bg-[#09090B] border border-white/10 rounded-xl px-3 text-sm mt-1">
                  <option value="24">24 horas</option><option value="12">12 horas</option>
                </select>
                <p className="text-[10px] text-zinc-600 mt-1">Abaixo disso, só via ⚡ Em cima da Hora.</p>
              </div>
            </div>
          </div>

          {feeCfg.can_edit && (
            <Button data-testid="fee-save-btn" onClick={openFeeConfirm} className="rounded-xl bg-[#FF4D00] hover:bg-[#E64500] gap-1"><Save className="h-3.5 w-3.5" />Salvar alterações</Button>
          )}

          <div className="pt-2">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Histórico de alterações</div>
            <div className="space-y-1.5" data-testid="fee-audit-list">
              {feeAudit.length === 0 ? <p className="text-sm text-zinc-500 py-4">Nenhuma alteração registrada.</p> :
                feeAudit.map((a) => (
                  <div key={a.id} className="bg-card border border-white/[0.06] rounded-xl p-3 text-xs flex flex-wrap items-center gap-2">
                    <span className="text-zinc-500 mono">{formatDateTime(a.created_at)}</span>
                    <span className="font-medium">{a.field_label}</span>
                    <span className="text-zinc-400">De <span className="text-zinc-300">{fmtFee(a.kind, a.old_value)}</span> → Para <span className="text-[#FF4D00]">{fmtFee(a.kind, a.new_value)}</span></span>
                    <span className="text-zinc-500 ml-auto">por {a.actor_email || "—"}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-card border-white/10" data-testid="fee-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar alteração de taxas</AlertDialogTitle>
            <AlertDialogDescription>Revise as mudanças abaixo. Elas valem apenas para transações NOVAS.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5 text-sm">
            {pendingChanges.map((c) => (
              <div key={c.key} className="flex items-center gap-2 bg-[#09090B] border border-white/[0.06] rounded-xl p-2">
                <span className="font-medium">{c.label}</span>
                <span className="text-zinc-400 flex items-center gap-1 ml-auto">{fmtFee(c.kind, c.from)} <ArrowRight className="h-3.5 w-3.5" /> <span className="text-[#FF4D00]">{fmtFee(c.kind, c.to)}</span></span>
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl bg-transparent border-white/10">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="fee-confirm-save" onClick={confirmSaveFees} className="rounded-xl bg-[#FF4D00] hover:bg-[#E64500]">Confirmar e salvar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {sub === "score" && scoreCfg && scoreDraft && (
        <div className="space-y-4 max-w-2xl" data-testid="score-config">
          <div className="flex items-center gap-2 text-sm font-semibold"><TrendingUp className="h-4 w-4 text-[#FF4D00]" />Critérios do Score Mí Chamou</div>
          <p className="text-[11px] text-zinc-500 -mt-2">Níveis: Iniciante → Intermediário (perfil completo + vídeo) → Avançado → Profissional (automático pelo Score). Ajuste os pesos e os limites de pontos para Avançado e Profissional.{!scoreCfg.can_edit && " Somente Super Admin pode alterar."}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[["w_events", "Peso: eventos realizados"], ["w_reviews", "Peso: nº de avaliações"], ["w_avg_rating", "Peso: média das avaliações"], ["w_checkins", "Peso: check-ins"], ["avancado_min_score", "Mín. pontos → Avançado"], ["profissional_min_score", "Mín. pontos → Profissional"], ["referral_points", "Pontos por indicação (convite)"]].map(([k, l]) => (
              <div key={k}>
                <Label className="text-[11px] text-zinc-500">{l}</Label>
                <Input data-testid={`score-${k}`} type="number" step="0.1" value={scoreDraft[k]} onChange={(e) => setScoreDraft({ ...scoreDraft, [k]: e.target.value })} disabled={!scoreCfg.can_edit} className="bg-[#09090B] border-white/10 rounded-xl mt-1" />
              </div>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-300" data-testid="score-referral-enabled">
            <Switch checked={!!scoreDraft.referral_enabled} onCheckedChange={(v) => setScoreDraft({ ...scoreDraft, referral_enabled: v })} disabled={!scoreCfg.can_edit} />
            Pontuar indicações (quando o convidado conclui o cadastro)
          </label>
          {scoreCfg.can_edit && <Button data-testid="score-save-btn" size="sm" onClick={saveScore} className="rounded-xl bg-[#FF4D00] hover:bg-[#E64500] gap-1"><Save className="h-3.5 w-3.5" />Salvar critérios</Button>}
        </div>
      )}

      {sub === "financeiro" && fin && (
        <div className="space-y-5" data-testid="fin-consolidated">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Receita</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[["MRR (recorrente)", money(fin.mrr), "text-emerald-400"],
                ["Receita transacional", money(fin.taxa_plataforma), "text-emerald-400"],
                ["Receita líquida", money((fin.receita_liquida || 0) + (fin.mrr || 0)), "text-emerald-400"],
                ["Custo Asaas (gateway)", money(fin.taxa_gateway), "text-red-400"]].map(([l, v, c]) => (
                <div key={l} className="bg-card border border-white/[0.06] rounded-xl p-4"><div className="text-[11px] text-zinc-500">{l}</div><div className={`text-xl font-bold mt-1 ${c}`}>{v}</div></div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Volume & Assinantes</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[["GMV (processado)", money(fin.total_processado)],
                ["Transações", fin.qtd_transacoes],
                ["Assinantes pagantes", fin.active_paid_subscribers],
                ["Churn", `${fin.churn_rate}%`],
                ["Cortesia / manual", fin.courtesy_manual_users],
                ["Gratuitos", fin.free_users],
                ["Repassado a músicos", money(fin.total_repassado_musicos)],
                ["Aguardando repasse", money(fin.aguardando_repasse)]].map(([l, v]) => (
                <div key={l} className="bg-card border border-white/[0.06] rounded-xl p-4"><div className="text-[11px] text-zinc-500">{l}</div><div className="text-xl font-bold mt-1">{v}</div></div>
              ))}
            </div>
          </div>
          <div className="text-[11px] text-zinc-600 flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" />Receita recorrente (MRR) e transacional são somadas separadamente. Cortesia/manual não entram no MRR. Custo Asaas = taxa de gateway das operações.</div>
        </div>
      )}

      {sub === "assinaturas" && subs && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[["MRR", money(subs.mrr)], ["Assinantes pagantes", subs.active_paid_subscribers], ["Cortesia/manual", subs.courtesy_manual_users], ["Gratuitos", subs.free_users]].map(([l, v]) => (
              <div key={l} className="bg-card border border-white/[0.06] rounded-xl p-4"><div className="text-[11px] text-zinc-500 uppercase">{l}</div><div className="text-xl font-bold mt-1">{v}</div></div>
            ))}
          </div>
          <div className="text-[11px] text-zinc-600 flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" />MRR considera apenas assinaturas ativas com cobrança (cortesia/manual não entram).</div>
          <div className="space-y-2">
            {subs.rows.length === 0 ? <p className="text-sm text-zinc-500 text-center py-6">Nenhuma assinatura.</p> :
              subs.rows.map((s) => (
                <div key={s.id} className="bg-card border border-white/[0.06] rounded-xl p-3 flex flex-wrap items-center gap-3 text-sm">
                  <span className="font-medium">{s.stage_name || s.user_email}</span>
                  <span className="text-zinc-500">{s.plan} · {money(s.value)} · {s.billing_type}</span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-xl border ${s.plan_status === "active" ? "border-emerald-500/40 text-emerald-400" : "border-amber-500/40 text-amber-400"}`}>{s.plan_status}</span>
                  <span className="text-[11px] text-zinc-600 mono ml-auto">{s.asaas_subscription_id}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {sub === "auditoria" && (
        <div className="space-y-3">
          <Button size="sm" variant="outline" onClick={exportCsv} className="rounded-xl bg-transparent border-white/10 gap-1"><Download className="h-3.5 w-3.5" />Exportar CSV</Button>
          <div className="space-y-1.5" data-testid="plans-audit-list">
            {audit.length === 0 ? <p className="text-sm text-zinc-500 text-center py-6">Nenhuma alteração de plano registrada.</p> :
              audit.map((a) => (
                <div key={a.id} className="bg-card border border-white/[0.06] rounded-xl p-3 text-xs">
                  <div className="flex flex-wrap gap-2 items-center">
                    <span className="text-zinc-500 mono">{formatDateTime(a.created_at)}</span>
                    <span className="font-medium">{a.user_email}</span>
                    <span className="text-zinc-400">{a.prev_plan} → <span className="text-[#FF4D00]">{a.new_plan}</span></span>
                    <span className="px-1.5 py-0.5 rounded-xl border border-white/10 text-zinc-400">{SRC_LABEL[a.new_source] || a.new_source}</span>
                    <span className="text-zinc-500 ml-auto">por {a.actor_email}</span>
                  </div>
                  {a.reason && <div className="text-zinc-500 mt-1">Motivo: {a.reason}{a.expires_at ? ` · até ${a.expires_at.slice(0, 10)}` : ""}</div>}
                </div>
              ))}
          </div>
        </div>
      )}

      {sub === "selo" && payer && (
        <div className="bg-card border border-white/[0.06] rounded-xl p-5 space-y-4 max-w-lg">
          <div className="flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="h-4 w-4 text-emerald-400" />Critérios do selo (cálculo automático)</div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[11px] text-zinc-500">Bom: mín. pagamentos</Label><Input type="number" value={payer.bom_min_events} onChange={(e) => setPayer({ ...payer, bom_min_events: parseInt(e.target.value) || 0 })} disabled={!canWrite} className="bg-[#09090B] border-white/10 rounded-xl mt-1" /></div>
            <div><Label className="text-[11px] text-zinc-500">Bom: % no prazo</Label><Input type="number" value={payer.bom_min_ontime} onChange={(e) => setPayer({ ...payer, bom_min_ontime: parseFloat(e.target.value) || 0 })} disabled={!canWrite} className="bg-[#09090B] border-white/10 rounded-xl mt-1" /></div>
            <div><Label className="text-[11px] text-zinc-500">Excelente: mín. pagamentos</Label><Input type="number" value={payer.excelente_min_events} onChange={(e) => setPayer({ ...payer, excelente_min_events: parseInt(e.target.value) || 0 })} disabled={!canWrite} className="bg-[#09090B] border-white/10 rounded-xl mt-1" /></div>
            <div><Label className="text-[11px] text-zinc-500">Excelente: % no prazo</Label><Input type="number" value={payer.excelente_min_ontime} onChange={(e) => setPayer({ ...payer, excelente_min_ontime: parseFloat(e.target.value) || 0 })} disabled={!canWrite} className="bg-[#09090B] border-white/10 rounded-xl mt-1" /></div>
          </div>
          {canWrite && <Button size="sm" onClick={savePayer} className="rounded-xl bg-[#FF4D00] hover:bg-[#E64500] gap-1"><Save className="h-3.5 w-3.5" />Salvar critérios</Button>}
        </div>
      )}
    </div>
  );
}
